import { defineStore } from 'pinia'
import { getFingerprint, getFingerprintData } from '@thumbmarkjs/thumbmarkjs'
import {
	createResourceItem,
	updateResourceItem,
	readResourceItem,
	readResources,
	RESOURCE_NAMES,
	FIELD_NAMES,
	convertToApiTimeDate,
	parseApiTimeDate,
} from '@/api/api.js'
import {
	DEBUG,
	SERVER_HOST_NAME
} from '@/app.conf.js'

const SESSION_ITEM_KEY = 'session'
const FILTERS_ITEM_KEY = 'filters'
const ORDERS_ITEM_KEY = 'orders'
const CHARTS_ITEM_KEY = 'charts'
const FINGERPRINTS_ITEM_KEY = 'fingerprints'

// Activity write period, seconds
const ACTIVITY_REWRITE_PERIOD = DEBUG ? 50 : 60 * 60
// Maximum session duration, seconds
const SESSION_DURATION = 60 * 60 * 24


export const useSessionStore = defineStore('sessionStore', {
    state: () => ({
		// Access keys for rest API (mongoDB)
		_api: {
			login: null,
			password: null,
		},

		session: { isOpen: null },
		filters: {},
		orders: {},
		charts: {},
		fingerprints: {},
	}),

    getters: {
		sessionSchema() {
			return Object.freeze({
				isOpen: 'isOpen',
				startTime: 'start_time',
				lastActivity: 'last_activity',
				stopTime: 'stop_time',
			})
		},

		_sessionTimeTypeFields() {
			return [
				this.sessionSchema.startTime,
				this.sessionSchema.lastActivity,
				this.sessionSchema.stopTime,
			]
		},

		fingerprintsSchema() {
			return Object.freeze({
				id: "_id",
				etag: "_etag",
				userId: 'user_id',
				browserId: 'browser_id',
				languages: 'languages',
				language: 'language',
				timezone: 'timezone',
				platform: 'platform',
				browser: 'browser',
				ip: 'ip',
				ipHistory: 'ip_history',
				city: 'city',
				cityHistory: 'city_history',
				country: 'country',
				countryHistory: 'country_history',
				region: 'region',
				regionHistory: 'region_history',
				location: 'location',
				locationHistory: 'location_history',
			})
		},

        isOpen() {
			return this.session[this.sessionSchema.isOpen] === true
        },

		userFingerprints() {
			return this.fingerprints
		}
    },

    actions: {
		async tryContinueSession(userId, login, password) {
			this.autosaveUpdates()

			this._api.login = login
			this._api.password = password

			await this.initSore(userId)
			if (!this.session[this.sessionSchema.isOpen]) {
				return false
			}

			if (!DEBUG && this.isSessionExpired()) {
				console.info('Session is expired')
				this.close()
				return false
			}

			try {
				await this._readFingerprints()
			}  catch (error) {
				console.error("on read fingerprints ", error)
			}

			await this.activity()

			this._saveToLocalStorage()

			return true
		},

        async open(userId, login=null, password=null) {
			console.info("Open session")

			if (login != null && password != null) {
				this._api.login = login
				this._api.password = password
			}

			this.fingerprints[this.fingerprintsSchema.userId] = userId

			this.session[this.sessionSchema.isOpen] = true
			this.session[this.sessionSchema.startTime] = new Date()

			try {
				await this._readFingerprints()
			}  catch (error) {
				console.error("on read fingerprints ", error)
			}

			// Close all sessions (except current) from curent browser
			let allSessions = await this.getUserSessions(
				this.fingerprints[this.fingerprintsSchema.userId],
				this.fingerprints[this.fingerprintsSchema.browserId]
			)
			allSessions = allSessions.filter(
				(el) => el[this.fingerprintsSchema.id] !== this.fingerprints[this.fingerprintsSchema.id]
					&& !el[this.sessionSchema.stopTime]
			)
			for (const session of allSessions) {
				const payload = {
					[this.sessionSchema.stopTime]: convertToApiTimeDate(session[this.sessionSchema.lastActivity]),
				}
				await updateResourceItem(
					RESOURCE_NAMES.sessions,
					this._api.login,
					this._api.password,
					session[FIELD_NAMES.id],
					session[FIELD_NAMES.etag],
					payload,
				).then(() => {
					return true
				}).catch(error => {
					console.error(`on close all sessions from curent browser: "${error}".`)
					return false
				})
			}

			//
			await this.activity()
        },

        async close() {
			console.info('Close session.')
			this.session[this.sessionSchema.isOpen] = false
			this.session[this.sessionSchema.stopTime] = new Date()
			await this._saveToServer()

			this.session = {
				[this.sessionSchema.isOpen]: false
			}
			this.fingerprints = {}
			this._saveToLocalStorage()
        },

		async activity() {
			console.log('Activity')
			const now = new Date().getTime() / 1000
			const last = this.session[this.sessionSchema.lastActivity] ?
				this.session[this.sessionSchema.lastActivity].getTime() / 1000 :
				0

			// If write period expires
			if (now - last > ACTIVITY_REWRITE_PERIOD
				&& this.session[this.sessionSchema.isOpen] === true
			) {
				console.info('Save activity to server')

				this.session[this.sessionSchema.lastActivity] = new Date()
				try {
					await this._readFingerprints()
				}  catch (error) {
					console.info("on read fingerprints ", error)
				}

				await this._saveToServer()
			}
		},

		isSessionExpired() {
			const now = new Date().getTime() / 1000
			const last = this.session[this.sessionSchema.lastActivity] ?
				this.session[this.sessionSchema.lastActivity].getTime() / 1000 :
				0

			return (now - last) > SESSION_DURATION
		},

		/* Return user last activity time */
		async getUserLastActivity(userId) {
			const sessions = await this.getUserSessions(userId)
			if (sessions.length == 0) {
				return undefined
			}

			const lastSession = sessions.sort(
				(a, b) => b[this.sessionSchema.lastActivity]?.getTime() - a[this.sessionSchema.lastActivity]?.getTime()
			)

			return lastSession[0] ? lastSession[0][this.sessionSchema.lastActivity] : undefined
		},

		async _readFingerprints() {
			// Read fingerprint
			const browserId = await getFingerprint()
			const fingerprints = await getFingerprintData()
			// console.log('finger prints', browserId, fingerprints)

			// Read from ip information from service throth proxy
			const res = await fetch('https://' + SERVER_HOST_NAME + '/ipinfo/')
			const ipMetrics = [
				[this.fingerprintsSchema.city, 'city'],
				[this.fingerprintsSchema.country, 'country'],
				[this.fingerprintsSchema.ip, 'query'],
				[this.fingerprintsSchema.region, 'regionName'],
			]

			const ipData = await res.json()
			// console.log('ip data', ipData)
			
			// Parse fingerprints
			//
			this.fingerprints[this.fingerprintsSchema.browserId] = browserId
			this.fingerprints[this.fingerprintsSchema.language] = fingerprints.locales?.languages
			this.fingerprints[this.fingerprintsSchema.timezone] = fingerprints.locales?.timezone
			this.fingerprints[this.fingerprintsSchema.platform] = fingerprints.system?.platform
			if (fingerprints.system?.browser) {
				this.fingerprints[this.fingerprintsSchema.browser] = 
					fingerprints.system?.browser?.name + ' ' + fingerprints.system?.browser?.version
			}

			this.fingerprints[this.fingerprintsSchema.location] = {
				'type': 'Point',
				'coordinates': [
					Number(ipData.lat),
					Number(ipData.lon),
				]
			}
			
			for (const keys of ipMetrics) {
				if (ipData[keys[1]]) {
					this.fingerprints[keys[0]] = ipData[keys[1]]
				}
			}

			// Copy location metric to history
			if (this.fingerprints[this.fingerprintsSchema.locationHistory] == undefined) {
				this.fingerprints[this.fingerprintsSchema.locationHistory] = []
				this.fingerprints[this.fingerprintsSchema.locationHistory].push(this.fingerprints[this.fingerprintsSchema.location])
			}
			const prevLocation = this.fingerprints[this.fingerprintsSchema.locationHistory][this.fingerprints[this.fingerprintsSchema.locationHistory].length - 1]
			if (prevLocation
				&& (prevLocation['coordinates'][0] != this.fingerprints[this.fingerprintsSchema.location]['coordinates'][0]
				|| prevLocation['coordinates'][1] != this.fingerprints[this.fingerprintsSchema.location]['coordinates'][1])
			) {
				this.fingerprints[this.fingerprintsSchema.locationHistory].push(this.fingerprints[this.fingerprintsSchema.location])
			}

			// Copy metrics to history
			const historyIpMetrics = [
				[this.fingerprintsSchema.cityHistory, 'city'],
				[this.fingerprintsSchema.countryHistory, 'country'],
				[this.fingerprintsSchema.ipHistory, 'query'],
				[this.fingerprintsSchema.regionHistory, 'region'],
			]
			for (const keys of historyIpMetrics) {
				let historyArr = this.fingerprints[keys[0]]
				
				if (ipData[keys[1]]
					&& (historyArr == undefined
						|| historyArr[historyArr.length - 1] != ipData[keys[1]])
				) {
					
					if (historyArr == undefined) {
						this.fingerprints[keys[0]] = []
						historyArr = this.fingerprints[keys[0]]
					}
					historyArr.push(ipData[keys[1]])
				}
			}

			// await this._saveToServer()

			// console.log('fingerprints', this.fingerprints)
		},

		// Returns an Array[String] of checked criterias,
		// or new Array() if no saved filters
		getFilter(page, filter) {
			if (!this.filters[page]) {
				this.filters[page] = {}
			}

			if (!this.filters[page][filter]) {
				this.filters[page][filter] = []
			}

			return this.filters[page][filter]
		},

		getOrder(page, object) {
			if (!this.orders[page]) {
				this.orders[page] = {}
			}

			if (!this.orders[page][object]) {
				this.orders[page][object] = {}
			}

			return this.orders[page][object]
		},

		getChartAxis(stationId, chart) {
			if (!this.charts[stationId]) {
				this.charts[stationId] = new Object
			}

			if (!this.charts[stationId][chart]) {
				this.charts[stationId][chart] = new Object
			}
	
			return this.charts[stationId][chart]
		},

		// setFilter() {},

		/*
		* Read last session for user
		*/
		async getLastSession(userId) {
			// console.log('get user session', userId)

			const sessions = await this.getUserSessions(userId)
			const sortedByTimeSessions = sessions.sort((a, b) => {
				return b[this.sessionSchema.lastActivity]?.getTime() 
					- a[this.sessionSchema.lastActivity]?.getTime()
			})
			// console.log('sorted by time sessions', sortedByTimeSessions)
			return sortedByTimeSessions[0]
		},

		/*
		* Read all user sessions
		*/
		async getUserSessions(userId, browserId=null) {
			const args = ['',]
			args[0] = `where={"${this.fingerprintsSchema.userId}": "${userId}"`
			if (browserId) {
				args[0] += `,"${this.fingerprintsSchema.browserId}": "${browserId}"`
			}
			args[0] += '}'

			const result = await readResources(
				RESOURCE_NAMES.sessions,
				this._api.login,
				this._api.password,
				args,
			).then(response => {
				for (const session of response) {
					// Convert API timedate to js Data object
					for (const key of this._sessionTimeTypeFields) {
						if (key in session) {
							session[key] = parseApiTimeDate(session[key])
						}
					}

					// Normalize location
					session[this.fingerprintsSchema.location] = session[this.fingerprintsSchema.location]?.coordinates
					session[this.fingerprintsSchema.locationHistory] =
						session[this.fingerprintsSchema.locationHistory]?.map(el => el.coordinates)
				}

				return response.sort((a, b) => 
					b[this.sessionSchema.lastActivity]?.getTime() - a[this.sessionSchema.lastActivity]?.getTime()
				)
			}).catch(error => {
				console.error(`on load last open session from server: "${error}".`)
				return false
			})

			return result
		},

		autosaveUpdates() {
			this.$subscribe(async (mutation, /*state*/) => {
				// Prevent update loop
				if (mutation.events && mutation.events.key[0] == '_') {
					return
				}

				this._saveToLocalStorage()
			})
		},

		async initSore(userId) {
			const _load = (key) => {
				const tmp = JSON.parse(localStorage.getItem(key))
				return tmp == null ? {} : tmp
			}
	
			// Get session from Local Storage
			const lsSession = JSON.parse(localStorage.getItem(SESSION_ITEM_KEY))
			if (lsSession && lsSession.isOpen) {
				this.session = lsSession
				// Convert string timedate to js Data object
				for (const key of this._sessionTimeTypeFields) {
					if (key in this.session) {
						this.session[key] = new Date(this.session[key])
					}
				}

				this.filters = _load(FILTERS_ITEM_KEY)
				this.orders = _load(ORDERS_ITEM_KEY)
				this.charts = _load(CHARTS_ITEM_KEY)
				this.fingerprints = _load(FINGERPRINTS_ITEM_KEY)
				
				// If no user id
				if (!(this.fingerprintsSchema.userId in this.fingerprints)) {
					//this.fingerprints[this.fingerprintsSchema.userId] = userId
					// Close session
					await this.close()
					return
				// If user id missmatch
				} else if(this.fingerprints[this.fingerprintsSchema.userId] != userId) {
					// Close session
					await this.close()
					return
				}
				// const fingerprints = _load(FINGERPRINTS_ITEM_KEY)
				// If object in local storage is not empty
				// if (this.fingerprintsSchema.userId in fingerprints) {
				// 	this.fingerprints = fingerprints
				// }
				const loadResult = await this._loadFromServer()

				if(!loadResult
					|| this.fingerprints[this.fingerprintsSchema.userId] != userId
					|| this.fingerprintsSchema.stopTime in this.fingerprints
				) {
					// Close session
					this.close()
					return
				}
			} else {
                this.session[this.sessionSchema.isOpen] = false
				localStorage.setItem(SESSION_ITEM_KEY, JSON.stringify(this.session))

				this.fingerprints = {}
				localStorage.setItem(FINGERPRINTS_ITEM_KEY, JSON.stringify(this.fingerprints))
            }
		},

		_saveToLocalStorage () {
			// console.log('Session storage. Save to local storage. Etag = ', this.fingerprints[FIELD_NAMES.etag])
			// Save to Local Storage
			localStorage.setItem(SESSION_ITEM_KEY, JSON.stringify(this.session))
			localStorage.setItem(FILTERS_ITEM_KEY, JSON.stringify(this.filters))
			localStorage.setItem(ORDERS_ITEM_KEY, JSON.stringify(this.orders))
			localStorage.setItem(CHARTS_ITEM_KEY, JSON.stringify(this.charts))
			localStorage.setItem(FINGERPRINTS_ITEM_KEY, JSON.stringify(this.fingerprints))
		},

		async _loadFromServer() {
			if (!this.fingerprints[this.fingerprintsSchema.id]) {
				return false
			}

			const result = await readResourceItem(
				RESOURCE_NAMES.sessions,
				this.fingerprints[this.fingerprintsSchema.id],
				this._api.login,
				this._api.password,
				//[`where={"${this.fingerprintsSchema.userId}": "${this.fingerprints[this.fingerprintsSchema.userId]}"}`],
			).then(response => {
				// console.log("Session storage: load session from server, response: ", response)
				this.fingerprints = response
				if (response._items[0]) {
					this.fingerprints.assign(response._items[0])
				}

				delete this.fingerprints[FIELD_NAMES.createTime]
				delete this.fingerprints[FIELD_NAMES.updateTime]
				delete this.fingerprints._links
				delete this.fingerprints._items

				// Convert API timedate to js Data object
				for (const key of this._sessionTimeTypeFields) {
					if (key in this.fingerprints) {
						this.session[key] = parseApiTimeDate(this.fingerprints[key])
						delete this.fingerprints[key]
					}
				}

				// console.log(`Load session id = ${this.fingerprints._id}, etag = ${this.fingerprints._etag}`)
				return true
			}).catch(error => {
				console.error(`on load session from server: "${error}".`)
				return false
			})

			return result
		},

		async _saveToServer() {
			if (!(this.fingerprintsSchema.userId in this.fingerprints)) {
				return
			}
			
			let payload = { ...this.fingerprints }
			payload[this.sessionSchema.startTime] = this.session[this.sessionSchema.startTime]
			payload[this.sessionSchema.lastActivity] = this.session[this.sessionSchema.lastActivity]
			payload[this.sessionSchema.stopTime] = this.session[this.sessionSchema.stopTime]

			// Enumerate all schema. Translate all Date to API datetime
			for (const [key, value] of Object.entries(payload)) {
				if (value instanceof Date) {
					payload[key] = convertToApiTimeDate(value)
				}
			}

			delete payload[FIELD_NAMES.etag]
			delete payload[FIELD_NAMES.id]
			delete payload[FIELD_NAMES.createTime]
			delete payload[FIELD_NAMES.updateTime]

			// console.log(`Save session id = ${this.fingerprints._id} etag = ${this.fingerprints._etag}`)
			if (this.fingerprints[FIELD_NAMES.id] && this.fingerprints[FIELD_NAMES.etag]) {
				// console.log(`Update resource ${RESOURCE_NAMES.sessions}, id=${this.fingerprints[FIELD_NAMES.id]} etag=${this.fingerprints[FIELD_NAMES.etag]}, payload=`, payload)
				return await updateResourceItem(
					RESOURCE_NAMES.sessions,
					this._api.login,
					this._api.password,
					this.fingerprints[FIELD_NAMES.id],
					this.fingerprints[FIELD_NAMES.etag],
					payload
				).then(result => {
					this.fingerprints[FIELD_NAMES.etag] = result[FIELD_NAMES.etag]
					return true
				}).catch(error => {
					console.error(`on save session to server: "${error}".`)
					this._loadFromServer()
					return false
				})
			} else {
				// console.log(`Create resource ${RESOURCE_NAMES.sessions}`)
				const resourceId = await createResourceItem(
					RESOURCE_NAMES.sessions,
					this._api.login,
					this._api.password,
					payload,
				)
				if (resourceId) {
					this.fingerprints[this.fingerprintsSchema.id] = resourceId
					return this._loadFromServer()
				} else {
					return false
				}
			}
		},
    },
})
